import { type FC } from 'react';
import { CopyIcon } from '../../../../../../../../assets/images';
import { EditingComponent } from '../../../../Tab1General/Components';
import {
  DTOgetOrderDetailedData,
  EDIT_FIELDS,
  IShipmentData,
  IShipmentDataWithoutShipments,
  SHIPMENT_PLATFORM,
  SHIPMENT_STATUS_ID
} from '../../../../../../../../types/orders';
import { displaySuccessNotification } from '../../../../../../../../utils/displayNudosStandardNotifications';
import { copyClipboard } from '../../../../../../../../utils/copyClipboard';
import { defaultPlace } from '../../../../../../../../utils/defaultPlace';
import { NudosHoverText, NudosSelectButton } from 'components/NudosComponents';
import useOrdersState from '../../../../../../../../state/useOrdersState';
import { LOCATION_PLACE_TYPE } from '../../../../../../../../types/services';
import ButtonAttention from 'utils/ButtonAttention/ButtonAttention';

import './ShipmentHeader.scss';

export interface ShipmentHeaderProps {
  shipment: IShipmentDataWithoutShipments;
  updateWithoutLoaders?: () => void;
  orderDetailedData?: DTOgetOrderDetailedData;
  listOfGroupedShipments?: IShipmentData[];
  updateWithLoaders?: () => void;
}

interface IshowReportResolvedOrEdit {
  reports: string[] | undefined;
  item: string;
}

type IFieldData = {
  flag?: string;
  value?: string;
};

/**
 * Renderiza el valor de un campo, mostrando la bandera si aplica y cambiando el color si está editado.
 * @param field - Datos del campo a mostrar
 * @param isEdited - Indica si el campo fue editado (aplica clase especial)
 * @returns JSX.Element
 */
const fieldValueText = (field: IFieldData, isEdited?: boolean) => {
  if (!field?.flag)
    return (
      <div className={`headerFieldValueText${isEdited ? ' headerFieldValueTextEdited' : ''}`}>{`${
        field?.value || '-'
      }`}</div>
    );
  return (
    <div className="headerFieldFlagContainer">
      <div className={`textCountryFlag${isEdited ? ' headerFieldValueTextEdited' : ''}`}>{field?.value || '-'}</div>
      <div className="flagFieldContainer" style={{ backgroundImage: `url(${field?.flag})` }} />
    </div>
  );
};

const ShipmentHeader: FC<ShipmentHeaderProps> = ({
  shipment,
  updateWithoutLoaders = () => {},
  orderDetailedData,
  listOfGroupedShipments,
  updateWithLoaders = () => {}
}) => {
  const { toggleAllProductsInShipmentSelection, isShipmentSelected } = useOrdersState();

  const getProductIdsFromShipment = (): number[] => {
    if (!listOfGroupedShipments) return [];
    return listOfGroupedShipments
      ?.flatMap(shipment => shipment.products || [])
      ?.map(product => product.id)
      ?.filter(id => id !== undefined) as number[];
  };

  const hasTemporaryStorage = listOfGroupedShipments?.some(
    shipment => !!shipment?.warehouseTemporaryStorage?.estimatedEntryDate
  );

  const showReportResolvedOrEdit = ({ reports = [], item = '' }: IshowReportResolvedOrEdit): boolean =>
    reports ? reports?.some(report => report === item) : false;
  const isEditedOrReported = (reports: string[], item: string, defaultClass: string = ''): string =>
    showReportResolvedOrEdit({ reports, item }) ? 'isEditField' : defaultClass;

  const productIds = getProductIdsFromShipment();
  const shipmentIsSelected = isShipmentSelected(shipment?.shipmentId);
  const notIsNudos = shipment?.place !== LOCATION_PLACE_TYPE.NUDOS;
  const isOffice = shipment?.place === LOCATION_PLACE_TYPE.OFFICE;
  const isEmployee = shipment.place === LOCATION_PLACE_TYPE.USER;
  const someShipmentsDataConfirmed = listOfGroupedShipments?.some(shipment => shipment?.statusId === SHIPMENT_STATUS_ID.CONFIRMED);

  const FIELD_LABELS = {
    START_DATE: 'Fecha de Ingreso',
    RECEIVER_NAME: 'Quien recibe',
    ID_DOCUMENT: 'Documento de identidad',
    PHONE_NUMBER: 'Número teléfono',
    COUNTRY: 'País',
    CITY: 'Ciudad',
    ZIP_CODE: 'Código postal',
    ADDRESS: 'Dirección',
    ADDITIONAL_REFERENCES: 'Indicaciones'
  } as const;

  const fieldMappings = [
    {
      item: '',
      label: FIELD_LABELS.START_DATE,
      value: shipment?.startDay || '-',
      defaultClass: 'headerField',
      show: isEmployee,
      size: 140
    },
    {
      item: EDIT_FIELDS.RECEIVER_NAME,
      label: FIELD_LABELS.RECEIVER_NAME,
      value: shipment?.receiverInfo?.receiverName || '',
      defaultClass: '',
      show: isOffice,
      size: 140
    },
    {
      item: EDIT_FIELDS.RECEIVER_ID_DOCUMENT,
      label: FIELD_LABELS.ID_DOCUMENT,
      value: shipment?.receiverInfo?.receiverIdDocument || '-',
      defaultClass: 'headerField',
      show: notIsNudos,
      size: 140
    },
    {
      item: EDIT_FIELDS.PHONE_NUMBER,
      label: FIELD_LABELS.PHONE_NUMBER,
      value: shipment?.receiverPhone || shipment?.receiverInfo?.receiverPhone || '',
      defaultClass: 'headerField',
      show: notIsNudos,
      size: 140
    },
    {
      item: '',
      label: FIELD_LABELS.COUNTRY,
      value: shipment?.country.name,
      defaultClass: 'headerField',
      show: true,
      size: 140,
      flag: shipment?.country?.flag
    },
    {
      item: EDIT_FIELDS.CITY,
      label: FIELD_LABELS.CITY,
      value: shipment?.city || '',
      defaultClass: 'headerField',
      show: true,
      size: 140
    },

    {
      item: EDIT_FIELDS.ZIP_CODE,
      label: FIELD_LABELS.ZIP_CODE,
      value: shipment?.zipCode || '',
      defaultClass: 'headerField',
      show: notIsNudos,
      size: 140
    },
    {
      item: EDIT_FIELDS.ADDRESS,
      label: FIELD_LABELS.ADDRESS,
      key: 'address',
      value: shipment?.deliveryAddress || '',
      defaultClass: 'headerField',
      show: notIsNudos,
      size: 480
    },
    {
      item: EDIT_FIELDS.ADDITIONAL_REFERENCES,
      label: FIELD_LABELS.ADDITIONAL_REFERENCES,
      value: shipment?.additionalReferences || '',
      defaultClass: 'headerField',
      show: notIsNudos,
      size: 140
    }
  ];

  const showSecondLine = fieldMappings?.slice(5)?.find(field => field?.value);

  const myCopyText = ({ shipment }: { shipment: IShipmentDataWithoutShipments }) => {
    displaySuccessNotification({
      customJSXMessage: <>Información de direccion copiada con éxito</>
    });
    const info: Record<string, string> = {};
    info.fullName = `Empleado: ${
      shipment?.receiverName ||
      shipment?.receiverInfo?.receiverName ||
      (shipment?.userInfo?.firstName && shipment?.userInfo?.lastName
        ? shipment?.userInfo?.firstName + ' ' + shipment?.userInfo?.lastName
        : '') ||
      ''
    }`;
    info.id = `ID: ${shipment?.receiverIdDocument || shipment?.receiverInfo?.receiverIdDocument || ''}`;
    info.zipCode = `Código postal: ${shipment?.zipCode || ''}`;
    info.phone = `Número de teléfono: ${
      shipment?.receiverPhone ||
      shipment?.receiverInfo?.receiverPhone ||
      (shipment?.userInfo?.phoneData?.phone &&
        shipment.userInfo.phoneData.phoneCode &&
        '+' + shipment?.userInfo?.phoneData?.phoneCode + ' ' + shipment?.userInfo?.phoneData?.phone) ||
      ''
    }`;
    info.city = `Ciudad: ${shipment?.city || ''}`;
    info.additionalReferences = `Indicaciones: ${shipment?.additionalReferences || ''}`;
    info.address = `Dirección: ${shipment?.deliveryAddress || ''}`;

    return Object.values(info).join('\n');
  };

  return (
    <div className="shipmentHeader">
      <div className="shipmentHeaderTitle">
        <div className="flex items-center gap-2">
          <div className="selectAllProductsInShipmentContainer">
            {notIsNudos && someShipmentsDataConfirmed && (
              <NudosSelectButton
                isButtonActive={shipmentIsSelected?.isShipmentSelected}
                handleClickButton={() =>
                  toggleAllProductsInShipmentSelection(
                    shipment?.shipmentId,
                    productIds,
                    shipment?.place,
                    shipment as IShipmentData,
                    hasTemporaryStorage,
                    listOfGroupedShipments?.filter(s => s?.statusId === SHIPMENT_STATUS_ID.CONFIRMED)
                  )
                }
                componentSize="xs"
                customClassName="w-3 h-3"
              />
            )}
            <span
              className="font-bold text-fg-default underline"
              style={{
                fontSize: '12px'
              }}
            >
              Envío {defaultPlace(shipment as IShipmentData)}
            </span>
          </div>

          <div className="shipmentCopyIconWrapper" onClick={() => copyClipboard({ text: myCopyText({ shipment }) })}>
            <CopyIcon />
            <div className="shipmentCopyIconTooltip">Copiar información de direcciones</div>
          </div>
          {!!shipment?.startDay && <ButtonAttention label="Nuevo ingreso" />}
        </div>
        {shipment?.place !== 'nudos' && (
          <EditingComponent
            orderId={Number(orderDetailedData?.orderId)}
            organizationId={Number(orderDetailedData?.generalData?.orgData?.organizationId)}
            organizationName={orderDetailedData?.generalData?.orgData?.organizationName}
            shipInfo={shipment as IShipmentData}
            updateWithoutLoaders={updateWithoutLoaders}
            customEditingButtonSize
            listOfGroupedShipments={listOfGroupedShipments}
            updateWithLoaders={updateWithLoaders}
          />
        )}
      </div>
      <div className="firstLineContainer">
        {fieldMappings.slice(0, 5).map((field, index) => {
          const isEdited =
            !!shipment.editShipments?.editedFields?.find(f => f === field?.item) &&
            shipment.editShipments?.platform === SHIPMENT_PLATFORM.NODI;
          const isNewEmployeeField = field.label === FIELD_LABELS.START_DATE && shipment?.startDay;

          return (
            field.show && (
              <div
                key={index}
                className={`${isEditedOrReported(
                  shipment?.editShipments?.editedFields as string[],
                  field.item,
                  field.defaultClass
                )} flex flex-col items-start ${isNewEmployeeField ? 'newEmployeeField' : ''}`}
                style={{ width: `${field?.size}px`, maxWidth: `${field?.size}px` }}
              >
                <div
                  className={`headerFieldText${isEdited ? ' headerFieldValueTextEdited' : ''} ${
                    isNewEmployeeField ? 'relative' : ''
                  }`}
                >
                  {isNewEmployeeField && (
                    <div className="newEmployeeIndicatorContainer">
                      <div className="newEmployeeIndicatorBackground" />
                      <div className="newEmployeeIndicator" />
                    </div>
                  )}
                  {field.label}
                </div>
                {fieldValueText(field, isEdited)}
              </div>
            )
          );
        })}
      </div>
      {showSecondLine && (
        <div className="secondLineContainer">
          {fieldMappings.slice(5).map((field, index) => {
            const isEdited =
              !!shipment.editShipments?.editedFields?.find(f => f === field?.item) &&
              shipment.editShipments?.platform === SHIPMENT_PLATFORM.NODI;

            const addressLimitText = 75;
            const additionalReferencesLimitText = 30;
            const addressMaxHoverWidth = '450px';
            const additionalReferencesMaxHoverWidth = '250px';

            return (
              field.show && (
                <div
                  key={index}
                  className={`${isEditedOrReported(
                    shipment?.editShipments?.editedFields as string[],
                    field.item,
                    field.defaultClass
                  )} flex flex-col items-start`}
                  style={{ width: `${field?.size}px`, maxWidth: `${field?.size}px` }}
                >
                  <div className={`headerFieldText ${isEdited && 'headerFieldValueTextEdited'}`}>{field.label}</div>
                  <div className={`headerFieldValueText ${isEdited && 'headerFieldValueTextEdited'}`}>
                    <NudosHoverText
                      maxHoverWidth={
                        field?.item === EDIT_FIELDS.ADDITIONAL_REFERENCES
                          ? additionalReferencesMaxHoverWidth
                          : addressMaxHoverWidth
                      }
                      text={field?.value || '-'}
                      charactersLimit={
                        field?.item === EDIT_FIELDS.ADDITIONAL_REFERENCES
                          ? additionalReferencesLimitText
                          : addressLimitText
                      }
                      onlyIfTruncated
                    />
                  </div>
                </div>
              )
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ShipmentHeader;
